/**
 * Route Override System
 *
 * Allows themes to override default route implementations using a registry pattern.
 * Compatible with Next.js static analysis and build-time optimization.
 *
 * Priority order:
 * 1. Theme-specific route component (registered in theme's route registry)
 * 2. Default route component
 *
 * Usage:
 * ```tsx
 * import { getThemeComponent } from '@/lib/route-loader';
 * const Component = getThemeComponent('categories', DefaultComponent);
 * ```
 */
import { ComponentType } from 'react';

import { logger } from '@/lib/logger';

export type RouteKey =
  // Shop routes
  | 'categories'
  | 'category-detail'
  | 'products'
  | 'product-detail'
  | 'product-reviews'
  | 'product-review'
  | 'search'
  | 'new-arrivals'
  | 'deals'
  | 'compare'
  | 'track-order'
  | 'track-order-detail'

  // Checkout routes
  | 'checkout'
  | 'checkout-shipping'
  | 'checkout-payment'
  | 'checkout-review'
  | 'checkout-success'
  | 'checkout-bank-transfer'

  // Account routes
  | 'account'
  | 'account-profile'
  | 'account-orders'
  | 'account-order-detail'
  | 'account-addresses'
  | 'account-wishlist'
  | 'account-payment-methods'
  | 'account-returns'
  | 'account-returns-create'

  // Auth routes
  | 'login'
  | 'login-business'
  | 'register'
  | 'register-business'
  | 'forgot-password'
  | 'reset-password'
  | 'verify-email'

  // Content routes
  | 'blog'
  | 'blog-category'
  | 'blog-post'

  // Support routes
  | 'support'
  | 'support-contact'
  | 'support-tickets'
  | 'support-ticket-detail'
  | 'faq'
  | 'faq-post'

  // Legal routes
  | 'about'
  | 'privacy'
  | 'terms'
  | 'shipping-policy'
  | 'return-policy'
  | 'careers'
  | 'sitemap'

  // System routes
  | 'home'
  | 'cart'
  | 'not-found';

/**
 * Theme Route Registry
 * Themes register their route overrides here
 */
type RouteRegistry = Map<string, Map<RouteKey, ComponentType<any>>>;

const themeRouteRegistry: RouteRegistry = new Map();

/**
 * Register a theme's route overrides
 * This should be called during theme initialization
 *
 * @example
 * ```tsx
 * // In themes/cray/routes/index.ts
 * import CategoriesPage from '../pages/categories/page';
 * import { registerThemeRoutes } from '@/lib/route-loader';
 *
 * registerThemeRoutes('cray', {
 *   'categories': CategoriesPage,
 *   'products': ProductsPage,
 * });
 * ```
 */
export function registerThemeRoutes(themeName: string, routes: Partial<Record<RouteKey, ComponentType<any>>>) {
  if (!themeRouteRegistry.has(themeName)) {
    themeRouteRegistry.set(themeName, new Map());
  }

  const themeRoutes = themeRouteRegistry.get(themeName)!;

  for (const [key, component] of Object.entries(routes)) {
    themeRoutes.set(key as RouteKey, component);
    logger.debug({
      msg: 'Registered theme route override',
      theme: themeName,
      route: key,
    });
  }
}

/**
 * Get the active theme name from environment or config
 */
export function getActiveTheme(): string {
  // Check environment variable first
  if (typeof process !== 'undefined' && process.env.NEXT_PUBLIC_THEME) {
    return process.env.NEXT_PUBLIC_THEME;
  }

  // Default to 'cray'
  return 'cray';
}

/**
 * Get a component for a route, checking theme overrides first
 *
 * @param routeKey - The route identifier
 * @param defaultComponent - The default component to use if no override exists
 * @param themeName - Optional theme name (defaults to active theme)
 * @returns The theme component or default component
 *
 * @example
 * ```tsx
 * // In a page file
 * import DefaultCategories from './default';
 * import { getThemeComponent } from '@/lib/route-loader';
 *
 * const CategoriesPage = getThemeComponent('categories', DefaultCategories);
 * export default CategoriesPage;
 * ```
 */
export function getThemeComponent<T extends ComponentType<any>>(routeKey: RouteKey, defaultComponent: T, themeName?: string): T {
  const theme = themeName || getActiveTheme();
  const themeRoutes = themeRouteRegistry.get(theme);

  logger.debug({
    msg: 'Getting theme component',
    theme,
    routeKey,
    hasThemeRoutes: !!themeRoutes,
    registeredThemes: Array.from(themeRouteRegistry.keys()),
    routesForTheme: themeRoutes ? Array.from(themeRoutes.keys()) : [],
  });

  if (!themeRoutes) {
    logger.debug({ msg: 'Theme has no route overrides', theme });
    return defaultComponent;
  }

  const themeComponent = themeRoutes.get(routeKey);

  if (themeComponent) {
    logger.debug({ msg: 'Using theme route override', theme, route: routeKey });
    return themeComponent as T;
  }

  logger.debug({ msg: 'Using default route component', route: routeKey, theme });
  return defaultComponent;
}

/**
 * Check if a theme has an override for a specific route
 */
export function hasThemeRoute(routeKey: RouteKey, themeName?: string): boolean {
  const theme = themeName || getActiveTheme();
  const themeRoutes = themeRouteRegistry.get(theme);
  return themeRoutes?.has(routeKey) || false;
}

/**
 * Get all route overrides provided by a theme
 */
export function getThemeRouteOverrides(themeName?: string): RouteKey[] {
  const theme = themeName || getActiveTheme();
  const themeRoutes = themeRouteRegistry.get(theme);

  if (!themeRoutes) {
    return [];
  }

  return Array.from(themeRoutes.keys());
}

/**
 * Get all registered themes
 */
export function getRegisteredThemes(): string[] {
  return Array.from(themeRouteRegistry.keys());
}

/**
 * Debug function to log the current state of the route registry
 */
export function debugRouteRegistry(): void {
  const themes = Array.from(themeRouteRegistry.keys());
  logger.debug('=== Route Registry Debug ===');
  logger.debug({ themes }, 'Registered themes:');

  themes.forEach((theme) => {
    const routes = themeRouteRegistry.get(theme);
    if (routes) {
      logger.debug({ routes: Array.from(routes.keys()) }, `Theme "${theme}" routes:`);
    }
  });
  logger.debug('===========================');
}
